// Sprint 4 Push 2: TerminBestaetigungTile.

import { useQuery } from "@tanstack/react-query";

import {
  TerminBestaetigungResponse,
  fetchTerminBestaetigung,
} from "../lib/api";
import { firmaDot } from "../lib/firma-colors";

const SKIP_LABEL: Record<string, string> = {
  vertrauen: "Stammkunde",
  wartungsvertrag: "Wartungsvertrag",
  zu_kurzfristig: "Zu kurzfristig",
  zu_frueh: "Zu früh",
};

export default function TerminBestaetigungTile() {
  const query = useQuery<TerminBestaetigungResponse, Error>({
    queryKey: ["termin-bestaetigung"],
    queryFn: fetchTerminBestaetigung,
    staleTime: 5 * 60_000,
  });

  if (query.isLoading) {
    return (
      <section className="rounded-2xl border border-slate-700/60 bg-slate-950 p-4 sm:p-5">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-slate-100">
          Termin-Bestätigung
        </h2>
        <p className="text-xs text-slate-400 mt-2">Lade …</p>
      </section>
    );
  }

  const items = query.data?.items ?? [];
  const nPending = query.data?.n_pending ?? 0;
  const nSkipped = query.data?.n_skipped ?? 0;

  // Phase E (Sprint 7 Final) Mobile-Polish: bei 0/0 komplett ausblenden.
  // Tile bringt nur Lärm wenn keine Termine + keine Skips anliegen.
  if (nPending === 0 && nSkipped === 0 && items.length === 0) {
    return null;
  }

  // Sprint 7 Push 1B: 1-Zeilen-Status. Erklär-Text in title-Tooltip (Hover-
  // Desktop, long-press-Mobile). Vorher: 3 Zeilen System-Erklärung.
  const statusTooltip =
    "Tooltime-Termin-Mirror (T1.11) zieht morgige Termine. Cron 17:00. " +
    "Anti-Spam-Filter (Stammkunde / Wartungsvertrag / <2T oder >7T → skip). " +
    "Live nach AVV-HalloPetra.";

  return (
    <section className="rounded-2xl border border-cyan-500/40 bg-slate-950 p-4 sm:p-5 space-y-2">
      <header
        className="flex items-baseline justify-between gap-2"
        title={statusTooltip}
      >
        <h2 className="text-sm font-semibold text-white">
          Termin-Bestätigung
          <span className="ml-2 text-[10px] uppercase tracking-wider text-neutral-500 font-normal">
            Läuft täglich 17:00
          </span>
        </h2>
        <span className="text-[10px] uppercase tracking-wider text-neutral-500">
          {nPending} pending · {nSkipped} skip
        </span>
      </header>

      {items.length > 0 && (
        <ul className="space-y-1">
          {items.slice(0, 3).map((it) => (
            <li
              key={it.id}
              className="px-2 py-1.5 flex items-baseline justify-between gap-2"
            >
              <span className="flex items-center gap-2 text-xs text-white min-w-0">
                <span
                  aria-hidden="true"
                  className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                    firmaDot(it.firma_id)
                  }`}
                />
                <span className="truncate">
                  {it.kunde_name ?? it.tooltime_termin_id}
                </span>
              </span>
              <span className="text-[10px] tabular-nums text-neutral-400 shrink-0">
                {it.anti_spam_skip_reason
                  ? SKIP_LABEL[it.anti_spam_skip_reason] ?? "skip"
                  : it.termin_datum.slice(8, 10) +
                    "." +
                    it.termin_datum.slice(5, 7)}
              </span>
            </li>
          ))}
          {items.length > 3 && (
            <li className="text-[10px] text-sky-300/80 pl-2">
              +{items.length - 3} weitere
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
